import {
  PartnerPageData,
  UserInfo,
  SelectionRecord,
  GameINFOV2,
  Round,
  RoundWithUserData,
  SimpleRound,
  PositionData,
  AdditionalAnswer,
} from 'types/types'
import { create } from 'zustand'
import {
  clearPendingCharityModal,
  hasPendingCharityModal,
  markCharityModalPending,
} from '@/utils/pendingCharityModalCache'

type ModalStore = {
  isLeagueModalOpen: boolean
  isJoinedGameModalOpen: boolean
  // gameId is optional context used to persist a "pending" charity modal
  // flag to localStorage — custom domains bounce through an extra redirect
  // after joining that can reset this in-memory store before the modal ever
  // renders, so we restore it from that flag. Whether the flag actually
  // matters is decided by the game page, which knows if charity is enabled.
  setIsJoinedGameModalOpen: (isJoinedGameModalOpen: boolean, gameId?: number) => void
  restorePendingCharityModal: (gameId: number, isCharityPageEnabled: boolean) => void
  setIsLeagueModalOpen: (isLeagueModalOpen: boolean) => void
  closeAllModals: () => void
}
type SelectionStore = {
  selections: SelectionRecord | null
  setSelections: (selections: SelectionRecord | null) => void
}

type MiniLeagueStore = {
  currentMiniLeague: GameINFOV2 | null
  setCurrentMiniLeague: (currentMiniLeague: GameINFOV2) => void
}

type ParentGameStore = {
  parentGame: GameINFOV2 | null
  setParentGame: (parentGame: GameINFOV2) => void
}

type PartnerPageDataStore = {
  partnerPageData: (PartnerPageData & { is_user_joined: boolean }) | null
  setPartnerPageData: (partnerPageData: PartnerPageData & { is_user_joined: boolean }) => void
}

type UserStore = {
  user: UserInfo | null
  setUser: (user: UserInfo | null) => void
}

type PositionStore = {
  positionData: PositionData | null
  setPositionData: (positionData: PositionData) => void
}

type RoundStore<T> = {
  selectedRound: T | null
  setSelectedRound: (selectedRound: T | null) => void
}

type SelectedTabStore = {
  selectedTab: 'predictions' | 'leaderboard' | 'stats' | null
  setSelectedTab: (selectedTab: 'predictions' | 'leaderboard' | 'stats' | null) => void
}

type LeaderboardSelectedViewStore = {
  selectedView: 'gameLeaderboard' | 'roundLeaderboard' | null
  setSelectedView: (selectedView: 'gameLeaderboard' | 'roundLeaderboard' | null) => void
}

type AdditionalAnswerStore = {
  additionalAnswer: AdditionalAnswer | null
  setAdditionalAnswer: (answer: AdditionalAnswer | null) => void
}

type GameRoundsStore = {
  gameId: number | null
  rounds: RoundWithUserData[]
  setRounds: (gameId: number, rounds: RoundWithUserData[]) => void
}

export type UnsavedChangesGuardVariant = 'predictions' | 'instant-win'

export type RequestNavigationOptions = {
  variant?: UnsavedChangesGuardVariant
  // Instant win only: how many purchased games the player hasn't played yet.
  unplayedCount?: number
  // Instant win only: settles all remaining games instantly (same action the
  // in-arena "Reveal All / Fast Play" control triggers).
  onRevealAllInstantWin?: () => void
}

type UnsavedChangesGuardStore = {
  isModalOpen: boolean
  pendingAction: (() => void) | null
  // Runs only on a true dismiss (backdrop/Escape) — e.g. restoring the
  // address bar after a cancelled browser Back/Forward.
  onCancelPending: (() => void) | null
  guardVariant: UnsavedChangesGuardVariant
  unplayedCount: number
  onRevealAllInstantWin: (() => void) | null
  requestNavigation: (
    action: () => void,
    onCancel?: () => void,
    options?: RequestNavigationOptions,
  ) => void
  closeGuard: () => void
  dismissGuard: () => void
  isLogoutInProgress: boolean
  setLogoutInProgress: (inProgress: boolean) => void
}

export const useModalStore = create<ModalStore>((set) => ({
  isLeagueModalOpen: false,
  isJoinedGameModalOpen: false,
  setIsLeagueModalOpen: (isLeagueModalOpen: boolean) => set({ isLeagueModalOpen }),
  setIsJoinedGameModalOpen: (isJoinedGameModalOpen, gameId) => {
    if (gameId !== undefined) {
      if (isJoinedGameModalOpen) {
        markCharityModalPending(gameId)
      } else {
        clearPendingCharityModal(gameId)
      }
    }
    set({ isJoinedGameModalOpen })
  },
  restorePendingCharityModal: (gameId, isCharityPageEnabled) => {
    if (isCharityPageEnabled && hasPendingCharityModal(gameId)) {
      set({ isJoinedGameModalOpen: true })
    }
  },
  closeAllModals: () =>
    set({
      isLeagueModalOpen: false,
      isJoinedGameModalOpen: false,
    }),
}))

export const useMiniLeagueStore = create<MiniLeagueStore>((set) => ({
  currentMiniLeague: null,
  setCurrentMiniLeague: (currentMiniLeague: GameINFOV2) =>
    set({
      currentMiniLeague,
    }),
}))

export const useParentGameStore = create<ParentGameStore>((set, get) => ({
  parentGame: null,
  setParentGame: (parentGame: GameINFOV2) => set({ parentGame }),
}))

export const usePartnerPageDataStore = create<PartnerPageDataStore>((set, get) => ({
  partnerPageData: null,
  setPartnerPageData: (partnerPageData: PartnerPageData & { is_user_joined: boolean }) =>
    set({ partnerPageData }),
}))

export const useSelectionsStore = create<SelectionStore>((set) => ({
  selections: null,
  setSelections: (selections: SelectionRecord | null) => set({ selections }),
}))

export const useUserStore = create<UserStore>((set) => ({
  user: null,
  setUser: (user: UserInfo | null) => set({ user }),
}))

export const usePositionStore = create<PositionStore>((set) => ({
  positionData: null,
  setPositionData: (positionData: any) => set({ positionData }),
}))

export const useSelectedRoundStore = create<RoundStore<SimpleRound>>((set) => ({
  selectedRound: null,
  setSelectedRound: (selectedRound) => set({ selectedRound }),
}))

export const useSelectedFullRoundStore = create<RoundStore<Round>>((set) => ({
  selectedRound: null,
  setSelectedRound: (selectedRound) => set({ selectedRound }),
}))

export const useLeaderboardSelectedRoundStore = create<RoundStore<Round>>((set) => ({
  selectedRound: null,
  setSelectedRound: (selectedRound) => set({ selectedRound }),
}))

export const useLeaderboardSimpleRoundStore = create<RoundStore<SimpleRound>>((set) => ({
  selectedRound: null,
  setSelectedRound: (selectedRound) => set({ selectedRound }),
}))

export const useSelectedTabStore = create<SelectedTabStore>((set) => ({
  selectedTab: null,
  setSelectedTab: (selectedTab: 'predictions' | 'leaderboard' | 'stats' | null) =>
    set({ selectedTab }),
}))

export const useLeaderboardSelectedViewStore = create<LeaderboardSelectedViewStore>((set) => ({
  selectedView: null,
  setSelectedView: (selectedView: 'gameLeaderboard' | 'roundLeaderboard' | null) =>
    set({ selectedView }),
}))

export const useAdditionalAnswerStore = create<AdditionalAnswerStore>((set) => ({
  additionalAnswer: null,
  setAdditionalAnswer: (additionalAnswer: AdditionalAnswer | null) => set({ additionalAnswer }),
}))

export const useGameRoundsStore = create<GameRoundsStore>((set) => ({
  gameId: null,
  rounds: [],
  setRounds: (gameId: number, rounds: RoundWithUserData[]) => set({ gameId, rounds }),
}))

// Pauses an in-app navigation (tab switch, route change) that would discard
// unsaved prediction edits, until the user confirms via UnsavedChangesModal.
export const useUnsavedChangesGuardStore = create<UnsavedChangesGuardStore>((set, get) => ({
  isModalOpen: false,
  pendingAction: null,
  onCancelPending: null,
  guardVariant: 'predictions',
  unplayedCount: 0,
  onRevealAllInstantWin: null,
  requestNavigation: (action, onCancel, options) =>
    set({
      isModalOpen: true,
      pendingAction: action,
      onCancelPending: onCancel ?? null,
      guardVariant: options?.variant ?? 'predictions',
      unplayedCount: options?.unplayedCount ?? 0,
      onRevealAllInstantWin: options?.onRevealAllInstantWin ?? null,
    }),
  closeGuard: () =>
    set({
      isModalOpen: false,
      pendingAction: null,
      onCancelPending: null,
      guardVariant: 'predictions',
      unplayedCount: 0,
      onRevealAllInstantWin: null,
    }),
  dismissGuard: () => {
    get().onCancelPending?.()
    set({
      isModalOpen: false,
      pendingAction: null,
      onCancelPending: null,
      guardVariant: 'predictions',
      unplayedCount: 0,
      onRevealAllInstantWin: null,
    })
  },
  isLogoutInProgress: false,
  setLogoutInProgress: (inProgress: boolean) => set({ isLogoutInProgress: inProgress }),
}))
